import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCustomerName } from "./customerName";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiCheckbox,
  EuiComboBox,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiIcon,
  EuiLoadingSpinner,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTextArea,
  EuiTitle,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "../../../auth";
import { useToast } from "src/notifications";
import { useAnketaI18n } from "src/internationalization";
import { useOpsanioCan } from "../../can";

/** Addresses attached to the questionnaire, grouped for the combo box. */
interface RecipientGroup {
  key: string;
  label: string;
  emails: string[];
}

/** One device kind of the appliance, as the delivery note will list it. */
interface DeliveryItem {
  key: string;
  label: string;
  count: number;
  devices: Array<{ name: string; model: string; serial: string }>;
}

/** What the delivery dialog pre-fills itself with; all server-derived. */
interface DeliveryDefaults {
  customerName: string;
  vpc: string;
  location: { address: string; displayName: string; comment: string };
  /** The complete appliance: servers, switch(es), DAC cables. */
  items: DeliveryItem[];
  summary: string;
  /** Internal technical/sales contacts copied on the request, server-side. */
  cc: string[];
  /**
   * The customer contact the request goes to - organizational, else sales, else
   * technical. `null` when the questionnaire names nobody; the dialog then asks
   * for one, which is the only case in which the server reads the typed values.
   */
  contact: { source: string; label: string; name: string; email: string } | null;
  /** True when `INSIDE_SALES_EMAIL` is configured; sending fails without it. */
  recipientConfigured: boolean;
}

/** Same shape the send endpoint validates against, so the dialog agrees with it. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The send endpoint rejects a longer message with 400; the editor stops first. */
const MESSAGE_MAX = 20000;

/**
 * The backend's own reason for a rejection, when it sent one. Axios only says
 * "Request failed with status code 400", which turned a validation error into
 * an unexplained failure; the route answers `{ error, details }`, so prefer
 * that.
 */
const backendError = (error: any): string | null => {
  const body = error?.service;
  const detail = body?.details ?? error?.details;
  const detailText = Array.isArray(detail)
    ? detail
        .map((issue: any) =>
          typeof issue === "string"
            ? issue
            : [issue?.path?.join?.("."), issue?.message]
                .filter(Boolean)
                .join(": "),
        )
        .filter(Boolean)
        .join("; ")
    : typeof detail === "string"
      ? detail
      : "";
  const headline = body?.error ?? error?.message ?? "";
  return [headline, detailText].filter(Boolean).join(" - ") || null;
};

/** The route answers errors as JSON, which arrives here as a Blob too. */
const readErrorFromBlob = async (b: Blob): Promise<string | null> => {
  if (!b.type.includes("json")) return null;
  try {
    const parsed = JSON.parse(await b.text());
    return parsed?.error || parsed?.message || "Unknown error";
  } catch {
    return "Unknown error";
  }
};

/**
 * The server's own reason for a failed blob request. With `responseType:
 * "blob"` the JSON error body arrives as a Blob - in `data` on a 2xx, in
 * `error.service` on anything else - so axios's "Request failed with status
 * code 503" would otherwise be all the operator ever sees.
 */
const blobError = async (response: {
  data?: unknown;
  error?: any;
}): Promise<string | null> => {
  const body =
    response.data instanceof Blob ? response.data : response.error?.service;
  return body instanceof Blob ? readErrorFromBlob(body) : null;
};

export const OpsanioApplianceFlyoutDocumentation = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => {
  const { Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();
  // The delivery request mails inside sales: opsanio/documentation · create.
  const canRequestDelivery = useOpsanioCan()(
    AnketaResourcesEnum.opsanioDocumentation,
    "create",
  );
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();

  // Shared with the offer dialog: a name corrected there is the name here.
  const [customerName, setCustomerName] = useCustomerName(
    appliance.id,
    appliance.displayName ?? "",
  );
  // Object URL of the rendered PDF - the same document the mail attaches.
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [sendOpen, setSendOpen] = useState(false);
  const [recipientGroups, setRecipientGroups] = useState<RecipientGroup[]>([]);
  const [selected, setSelected] = useState<Array<{ label: string }>>([]);
  const [note, setNote] = useState("");
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [sending, setSending] = useState(false);

  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryDefaults, setDeliveryDefaults] = useState<DeliveryDefaults | null>(
    null,
  );
  const [deliveryCustomer, setDeliveryCustomer] = useState("");
  const [deliveryContactName, setDeliveryContactName] = useState("");
  const [deliveryContactEmail, setDeliveryContactEmail] = useState("");
  /** Book the freight forwarder through inside sales, not only the hardware. */
  const [deliveryShipping, setDeliveryShipping] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState("");
  const [deliverySending, setDeliverySending] = useState(false);

  const canFetch = useMemo(() => !!keycloak?.token, [keycloak]);

  const safeName = useMemo(
    () =>
      customerName
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .toLowerCase(),
    [customerName],
  );

  /** The documentation route; `&format=pdf` asks for the PDF. */
  const documentPath = useMemo(
    () =>
      `v2/${AnketaResourcesEnum.documentation}/network/${appliance.catalogId}/${appliance.id}?customerName=${encodeURIComponent(
        customerName.trim(),
      )}`,
    [appliance.catalogId, appliance.id, customerName],
  );

  const toast = useCallback(
    (key: string, fallback: string, text: string, color: "danger" | "warning") =>
      addToast({
        title: translate(key, { defaultMessage: fallback }),
        color,
        text,
      }),
    [addToast, translate],
  );

  const generate = useCallback(async () => {
    if (!canFetch) {
      toast(
        "anketa.opsanio.appliance.flyout.documentation.session.expired.title",
        "Session expired",
        translate(
          "anketa.opsanio.appliance.flyout.documentation.session.expired.text",
          { defaultMessage: "It seems that your session has been expired" },
        ),
        "danger",
      );
      return;
    }

    if (!customerName.trim()) {
      toast(
        "anketa.opsanio.appliance.flyout.documentation.missingData.title",
        "Missing Data",
        translate(
          "anketa.opsanio.appliance.flyout.documentation.missingData.text",
          { defaultMessage: "Please enter customer name" },
        ),
        "warning",
      );
      return;
    }

    setLoading(true);
    // Release the previous document before replacing it.
    setPdfUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });

    const response = await Get<Blob>({
      path: `${documentPath}&format=pdf`,
      responseType: "blob",
      token: keycloak.token,
    });

    const received = response.data;
    const serverError = await blobError(response);

    setLoading(false);
    // The API client never throws on HTTP errors - `success` is the check.
    if (!response.success || serverError || !(received instanceof Blob)) {
      toast(
        "anketa.opsanio.appliance.flyout.documentation.downloadError.title",
        "Documentation Error",
        serverError ||
          backendError(response.error) ||
          translate(
            "anketa.opsanio.appliance.flyout.documentation.downloadError.text",
            { defaultMessage: "Could not generate the document." },
          ),
        "danger",
      );
      return;
    }

    if (received.size === 0) {
      toast(
        "anketa.opsanio.appliance.flyout.documentation.downloadError.title",
        "Documentation Error",
        translate(
          "anketa.opsanio.appliance.flyout.documentation.empty.text",
          { defaultMessage: "The server returned an empty document." },
        ),
        "danger",
      );
      return;
    }

    setPdfUrl(
      URL.createObjectURL(received.slice(0, received.size, "application/pdf")),
    );
  }, [customerName, documentPath, canFetch, keycloak, Get, toast, translate]);

  // Nothing should outlive the component holding the object URL.
  useEffect(
    () => () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl],
  );

  const comboOptions = useMemo(
    () =>
      recipientGroups.map((group) => ({
        label: group.label,
        options: group.emails.map((email) => ({ label: email })),
      })),
    [recipientGroups],
  );

  /**
   * The addresses the server copies on every send: the questionnaire's internal
   * technical and sales contacts. Read from the same groups the combo box is
   * built from, so the dialog names exactly who the route will add.
   */
  const alwaysCopied = useMemo(
    () =>
      Array.from(
        new Set(
          recipientGroups
            .filter((group) => group.key === "technical" || group.key === "sales")
            .flatMap((group) => group.emails),
        ),
      ),
    [recipientGroups],
  );

  const openSendDialog = useCallback(async () => {
    setSendOpen(true);
    setLoadingRecipients(true);

    const response = await Get<any>({
      path: `v2/${AnketaResourcesEnum.documentation}/recipients/${appliance.catalogId}/${appliance.id}`,
      token: keycloak?.token,
    });

    // The route answers with the AnketaApiResponse envelope, so the payload
    // arrives as `data.data`; accept a bare array too in case that changes.
    const payload = response.data;
    const groups: RecipientGroup[] = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    setLoadingRecipients(false);
    if (!response.success) {
      setRecipientGroups([]);
      toast(
        "anketa.opsanio.appliance.flyout.documentation.recipientsError.title",
        "Recipients",
        backendError(response.error) ||
          translate(
            "anketa.opsanio.appliance.flyout.documentation.recipientsError.text",
            { defaultMessage: "Could not load the possible recipients." },
          ),
        "danger",
      );
      return;
    }
    setRecipientGroups(groups);
  }, [appliance.catalogId, appliance.id, Get, keycloak, toast, translate]);

  /** Accept a typed-in address, provided it actually looks like one. */
  const addTypedRecipient = useCallback(
    (searchValue: string) => {
      const email = searchValue.trim().toLowerCase();
      if (!EMAIL_RE.test(email)) return false;
      setSelected((current) =>
        current.some((option) => option.label === email)
          ? current
          : [...current, { label: email }],
      );
      return true;
    },
    [],
  );

  const closeSendDialog = useCallback(() => {
    setSendOpen(false);
    setSelected([]);
    setNote("");
  }, []);

  const send = useCallback(async () => {
    if (selected.length === 0) return;
    setSending(true);

    const response = await Post({
      path: `v2/${AnketaResourcesEnum.documentation}/network/${appliance.catalogId}/${appliance.id}/send`,
      token: keycloak?.token,
      value: {
        customerName: customerName.trim(),
        recipients: selected.map((option) => option.label),
        ...(note.trim() ? { message: note.trim() } : {}),
      },
    });

    setSending(false);

    if (!response.success) {
      toast(
        "anketa.opsanio.appliance.flyout.documentation.sendError.title",
        "Send Error",
        backendError(response.error) ||
          translate("anketa.opsanio.appliance.flyout.documentation.sendError.text", {
            defaultMessage: "The document could not be sent.",
          }),
        "danger",
      );
      return;
    }

    addToast({
      title: translate(
        "anketa.opsanio.appliance.flyout.documentation.sendSuccess.title",
        { defaultMessage: "Documentation sent" },
      ),
      color: "success",
      text: selected.map((option) => option.label).join(", "),
    });
    closeSendDialog();
  }, [
    selected,
    note,
    customerName,
    appliance.catalogId,
    appliance.id,
    Post,
    keycloak,
    addToast,
    toast,
    translate,
    closeSendDialog,
  ]);

  /**
   * Open the delivery dialog and load its defaults. Everything shown is
   * derived from the questionnaire server-side, so a stale browser state cannot
   * put a wrong address or contact into the request.
   */
  const openDeliveryDialog = useCallback(async () => {
    setDeliveryOpen(true);
    setDeliveryLoading(true);
    setDeliveryDefaults(null);
    setDeliveryContactName("");
    setDeliveryContactEmail("");
    setDeliveryShipping(false);
    setDeliveryNote("");

    const response = await Get<any>({
      path: `v2/${AnketaResourcesEnum.documentation}/delivery/${appliance.catalogId}/${appliance.id}`,
      token: keycloak?.token,
    });

    setDeliveryLoading(false);

    const payload = response.data?.data ?? response.data;
    if (!response.success || !payload) {
      toast(
        "anketa.opsanio.appliance.flyout.documentation.delivery.loadError.title",
        "Delivery request",
        backendError(response.error) ||
          translate(
            "anketa.opsanio.appliance.flyout.documentation.delivery.loadError.text",
            { defaultMessage: "Could not load the delivery details." },
          ),
        "danger",
      );
      return;
    }

    const defaults = payload as DeliveryDefaults;
    setDeliveryDefaults(defaults);
    // The customer name defaults to the questionnaire name, but the operator
    // may have corrected it above for the document - keep the two in step.
    setDeliveryCustomer(customerName.trim() || defaults.customerName);
  }, [
    appliance.catalogId,
    appliance.id,
    customerName,
    Get,
    keycloak,
    toast,
    translate,
  ]);

  const closeDeliveryDialog = useCallback(() => {
    setDeliveryOpen(false);
    setDeliveryDefaults(null);
    setDeliveryContactName("");
    setDeliveryContactEmail("");
    setDeliveryShipping(false);
    setDeliveryNote("");
  }, []);

  /**
   * A contact is needed either way: from the questionnaire or typed in here.
   * A name or an address is enough - the server accepts either.
   */
  const deliveryContactMissing =
    !!deliveryDefaults &&
    !deliveryDefaults.contact &&
    deliveryContactName.trim() === "" &&
    deliveryContactEmail.trim() === "";

  const sendDeliveryRequest = useCallback(async () => {
    if (!deliveryDefaults || deliveryCustomer.trim() === "") return;
    setDeliverySending(true);

    const response = await Post({
      path: `v2/${AnketaResourcesEnum.documentation}/delivery/${appliance.catalogId}/${appliance.id}`,
      token: keycloak?.token,
      value: {
        customerName: deliveryCustomer.trim(),
        // Only read by the server when the questionnaire names nobody.
        ...(deliveryDefaults.contact
          ? {}
          : {
              contactName: deliveryContactName.trim(),
              ...(deliveryContactEmail.trim()
                ? { contactEmail: deliveryContactEmail.trim() }
                : {}),
            }),
        // Always sent, in both states: the mail says either way whether inside
        // sales is expected to book the forwarder.
        arrangeShipping: deliveryShipping,
        ...(deliveryNote.trim() ? { note: deliveryNote.trim() } : {}),
      },
    });

    setDeliverySending(false);

    if (!response.success) {
      toast(
        "anketa.opsanio.appliance.flyout.documentation.delivery.sendError.title",
        "Delivery request",
        backendError(response.error) ||
          translate(
            "anketa.opsanio.appliance.flyout.documentation.delivery.sendError.text",
            { defaultMessage: "The delivery request could not be sent." },
          ),
        "danger",
      );
      return;
    }

    addToast({
      title: translate(
        "anketa.opsanio.appliance.flyout.documentation.delivery.sent.title",
        { defaultMessage: "Delivery requested" },
      ),
      color: "success",
      text: deliveryShipping
        ? translate(
            "anketa.opsanio.appliance.flyout.documentation.delivery.sent.shipping",
            {
              defaultMessage:
                "Inside sales was asked for the appliance and the freight forwarding.",
            },
          )
        : translate("anketa.opsanio.appliance.flyout.documentation.delivery.sent.text", {
            defaultMessage: "Inside sales was asked to provide the appliance.",
          }),
    });
    closeDeliveryDialog();
  }, [
    deliveryDefaults,
    deliveryCustomer,
    deliveryContactName,
    deliveryContactEmail,
    deliveryShipping,
    deliveryNote,
    appliance.catalogId,
    appliance.id,
    Post,
    keycloak,
    addToast,
    toast,
    translate,
    closeDeliveryDialog,
  ]);

  const onNameChange = useCallback(
    (value: string) => setCustomerName(value),
    [setCustomerName],
  );

  // The preview belongs to the name it was rendered for - whether the name
  // changed here, in the offer dialog, or because another appliance was
  // selected - so drop it rather than show a stale one. The name itself is
  // keyed by appliance in the shared store, so switching appliances needs no
  // re-seeding.
  useEffect(() => {
    setPdfUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
  }, [appliance.id, customerName]);

  return (
    <EuiPanel paddingSize="m" hasShadow={false} color="subdued">
      <EuiFlexGroup direction="column" gutterSize="m">
        <EuiFlexItem grow={false}>
          <EuiTitle size="xs">
            <h4>
              {translate("anketa.opsanio.appliance.flyout.documentation.title", {
                defaultMessage: "Generate Documentation",
              })}
            </h4>
          </EuiTitle>
          <EuiSpacer size="s" />
          <EuiFlexGroup gutterSize="s" alignItems="center">
            <EuiFlexItem>
              <EuiFieldText
                placeholder={translate(
                  "anketa.opsanio.appliance.flyout.documentation.customerName.placeholder",
                  { defaultMessage: "Enter customer name" },
                )}
                value={customerName}
                onChange={(e) => onNameChange(e.target.value)}
                disabled={loading}
              />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton onClick={generate} fill isLoading={loading}>
                {translate(
                  "anketa.opsanio.appliance.flyout.documentation.preview.button",
                  { defaultMessage: "Preview" },
                )}
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              {/*
                The very blob the preview shows, so what is filed here and what
                the recipient opens cannot drift apart.
              */}
              <EuiButton
                iconType="download"
                href={pdfUrl ?? undefined}
                download={`${safeName}-appliance-dokumentation.pdf`}
                isDisabled={!pdfUrl || loading}
              >
                {translate(
                  "anketa.opsanio.appliance.flyout.documentation.download.pdf",
                  { defaultMessage: "Download PDF" },
                )}
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton
                onClick={openSendDialog}
                iconType="email"
                isDisabled={!customerName.trim() || loading}
              >
                {translate(
                  "anketa.opsanio.appliance.flyout.documentation.send.button",
                  { defaultMessage: "Send by e-mail" },
                )}
              </EuiButton>
            </EuiFlexItem>
            {canRequestDelivery && (
            <EuiFlexItem grow={false}>
              <EuiButton onClick={openDeliveryDialog} iconType="package">
                {translate(
                  "anketa.opsanio.appliance.flyout.documentation.delivery.button",
                  { defaultMessage: "Request delivery" },
                )}
              </EuiButton>
            </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>

        {loading && (
          <EuiFlexItem grow={false}>
            <EuiFlexGroup
              justifyContent="center"
              alignItems="center"
              style={{ minHeight: 320 }}
            >
              <EuiFlexItem grow={false}>
                <EuiLoadingSpinner size="xl" />
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        )}

        {pdfUrl && !loading && (
          <EuiFlexItem>
            <iframe
              title={translate(
                "anketa.opsanio.appliance.flyout.documentation.preview.title",
                { defaultMessage: "Documentation preview" },
              )}
              src={pdfUrl}
              style={{
                width: "100%",
                height: "75vh",
                border: "1px solid #d3dae6",
                borderRadius: 4,
              }}
            />
          </EuiFlexItem>
        )}
      </EuiFlexGroup>

      {sendOpen && (
        <EuiModal onClose={closeSendDialog} maxWidth={640} style={{ width: 640 }}>
          <EuiModalHeader>
            <EuiTitle size="s">
              <h2>
                <EuiIcon type="email" size="l" style={{ marginRight: 8 }} />
                {translate(
                  "anketa.opsanio.appliance.flyout.documentation.send.title",
                  { defaultMessage: "Send documentation" },
                )}
              </h2>
            </EuiTitle>
          </EuiModalHeader>

          <EuiModalBody>
            <EuiText size="s" color="subdued">
              <p>
                {translate(
                  "anketa.opsanio.appliance.flyout.documentation.send.intro",
                  {
                    defaultMessage:
                      "The document is attached as a PDF. Addresses from this questionnaire are suggested; you can also enter another one.",
                  },
                )}
              </p>
            </EuiText>
            <EuiSpacer size="m" />
            <EuiFormRow
              fullWidth
              label={translate(
                "anketa.opsanio.appliance.flyout.documentation.send.recipients.label",
                { defaultMessage: "Recipients" },
              )}
            >
              <EuiComboBox
                fullWidth
                isLoading={loadingRecipients}
                options={comboOptions}
                selectedOptions={selected}
                onChange={(options: Array<{ label: string }>) =>
                  setSelected(options)
                }
                // Any valid address may be typed in, not only the ones already
                // attached to the questionnaire.
                onCreateOption={addTypedRecipient}
                customOptionText={translate(
                  "anketa.opsanio.appliance.flyout.documentation.send.recipients.custom",
                  { defaultMessage: "Add {searchValue} as a recipient" },
                )}
                placeholder={translate(
                  "anketa.opsanio.appliance.flyout.documentation.send.recipients.placeholder",
                  { defaultMessage: "Select or enter recipients" },
                )}
                isClearable
              />
            </EuiFormRow>
            {/*
              The internal contacts are copied by the server whatever is picked
              here, so name them rather than let them surprise the sender in the
              mail header.
            */}
            {alwaysCopied.length > 0 && (
              <>
                <EuiSpacer size="xs" />
                <EuiText size="xs" color="subdued">
                  {translate(
                    "anketa.opsanio.appliance.flyout.documentation.send.cc",
                    {
                      values: { addresses: alwaysCopied.join(", ") },
                      defaultMessage:
                        "Always copied (technical / sales contact): {{addresses}}",
                    },
                  )}
                </EuiText>
              </>
            )}
            <EuiSpacer size="m" />
            <EuiFormRow
              fullWidth
              label={translate(
                "anketa.opsanio.appliance.flyout.documentation.send.note.label",
                { defaultMessage: "Message (optional)" },
              )}
              // The endpoint caps the message at 5000 characters and answers a
              // longer one with 400, which used to surface as an unexplained
              // failure. Say the limit and enforce it here.
              helpText={translate(
                "anketa.opsanio.appliance.flyout.documentation.send.note.help",
                {
                  values: { used: note.length, max: MESSAGE_MAX },
                  defaultMessage:
                    "Markdown. Appears above the attachment note. {{used}} of {{max}} characters.",
                },
              )}
              isInvalid={note.length > MESSAGE_MAX}
            >
              <EuiTextArea
                fullWidth
                rows={4}
                value={note}
                maxLength={MESSAGE_MAX}
                isInvalid={note.length > MESSAGE_MAX}
                onChange={(e) => setNote(e.target.value)}
              />
            </EuiFormRow>
          </EuiModalBody>

          <EuiModalFooter>
            <EuiButtonEmpty onClick={closeSendDialog} isDisabled={sending}>
              {translate("anketa.opsanio.appliance.flyout.documentation.send.cancel", {
                defaultMessage: "Cancel",
              })}
            </EuiButtonEmpty>
            <EuiButton
              fill
              onClick={send}
              isLoading={sending}
              isDisabled={selected.length === 0}
            >
              {translate("anketa.opsanio.appliance.flyout.documentation.send.confirm", {
                defaultMessage: "Send",
              })}
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      )}

      {deliveryOpen && (
        <EuiModal onClose={closeDeliveryDialog} maxWidth={640} style={{ width: 640 }}>
          <EuiModalHeader>
            <EuiTitle size="s">
              <h2>
                <EuiIcon type="package" size="l" style={{ marginRight: 8 }} />
                {translate(
                  "anketa.opsanio.appliance.flyout.documentation.delivery.title",
                  { defaultMessage: "Request delivery" },
                )}
              </h2>
            </EuiTitle>
          </EuiModalHeader>

          <EuiModalBody>
            {deliveryLoading && (
              <EuiFlexGroup justifyContent="center" style={{ minHeight: 120 }}>
                <EuiFlexItem grow={false}>
                  <EuiLoadingSpinner size="l" />
                </EuiFlexItem>
              </EuiFlexGroup>
            )}

            {!deliveryLoading && deliveryDefaults && (
              <>
                <EuiText size="s" color="subdued">
                  <p>
                    {translate(
                      "anketa.opsanio.appliance.flyout.documentation.delivery.intro",
                      {
                        defaultMessage:
                          "Asks inside sales to deliver the complete appliance. Devices, site and contact person are taken from the questionnaire and travel with the request.",
                      },
                    )}
                  </p>
                  {deliveryDefaults.cc?.length > 0 && (
                    <p>
                      {translate(
                        "anketa.opsanio.appliance.flyout.documentation.delivery.cc",
                        {
                          values: { addresses: deliveryDefaults.cc.join(", ") },
                          defaultMessage:
                            "Copied (technical / sales contact): {{addresses}}",
                        },
                      )}
                    </p>
                  )}
                </EuiText>

                {!deliveryDefaults.recipientConfigured && (
                  <>
                    <EuiSpacer size="s" />
                    <EuiCallOut
                      size="s"
                      color="danger"
                      iconType="alert"
                      title={translate(
                        "anketa.opsanio.appliance.flyout.documentation.delivery.noRecipient",
                        {
                          defaultMessage:
                            "No inside sales address is configured (INSIDE_SALES_EMAIL) - the request cannot be sent.",
                        },
                      )}
                    />
                  </>
                )}

                <EuiSpacer size="m" />
                <EuiFormRow
                  fullWidth
                  label={translate(
                    "anketa.opsanio.appliance.flyout.documentation.delivery.customer.label",
                    { defaultMessage: "Customer" },
                  )}
                >
                  <EuiFieldText
                    fullWidth
                    value={deliveryCustomer}
                    onChange={(e) => setDeliveryCustomer(e.target.value)}
                  />
                </EuiFormRow>

                <EuiSpacer size="m" />
                <EuiFormRow
                  fullWidth
                  label={translate(
                    "anketa.opsanio.appliance.flyout.documentation.delivery.scope.label",
                    { defaultMessage: "Scope of delivery" },
                  )}
                  helpText={translate(
                    "anketa.opsanio.appliance.flyout.documentation.delivery.scope.help",
                    {
                      defaultMessage:
                        "The complete appliance as the questionnaire describes it. Serial numbers travel with the request.",
                    },
                  )}
                >
                  <EuiText size="s">
                    <p style={{ margin: 0 }}>
                      <strong>{deliveryDefaults.summary}</strong>
                    </p>
                    {deliveryDefaults.items.map((item) => (
                      <p key={item.key} style={{ margin: 0 }}>
                        {item.count > 0 ? (
                          <>
                            {item.count}× {item.label}
                            <EuiText size="xs" color="subdued">
                              {item.devices
                                .map(
                                  (device) =>
                                    [device.name, device.model, device.serial]
                                      .filter(Boolean)
                                      .join(" · ") ||
                                    translate(
                                      "anketa.opsanio.appliance.flyout.documentation.delivery.scope.unlabelled",
                                      { defaultMessage: "no identification" },
                                    ),
                                )
                                .join(" | ")}
                            </EuiText>
                          </>
                        ) : (
                          // Named rather than hidden: an empty line is a gap in
                          // the questionnaire, not a delivery without switches.
                          <EuiText size="xs" color="warning">
                            {translate(
                              "anketa.opsanio.appliance.flyout.documentation.delivery.scope.missing",
                              {
                                values: { label: item.label },
                                defaultMessage:
                                  "{{label}}: not yet assigned in the questionnaire",
                              },
                            )}
                          </EuiText>
                        )}
                      </p>
                    ))}
                  </EuiText>
                </EuiFormRow>

                <EuiSpacer size="m" />
                <EuiFormRow
                  fullWidth
                  label={translate(
                    "anketa.opsanio.appliance.flyout.documentation.delivery.location.label",
                    { defaultMessage: "Delivery address" },
                  )}
                  helpText={translate(
                    "anketa.opsanio.appliance.flyout.documentation.delivery.location.help",
                    {
                      defaultMessage:
                        "The primary site of this questionnaire. Correct it there, not here.",
                    },
                  )}
                >
                  <EuiText size="s">
                    <p style={{ margin: 0 }}>
                      <strong>{deliveryDefaults.location.address}</strong>
                    </p>
                    {deliveryDefaults.location.displayName !==
                      deliveryDefaults.location.address && (
                      <p style={{ margin: 0 }}>
                        {deliveryDefaults.location.displayName}
                      </p>
                    )}
                    {deliveryDefaults.location.comment && (
                      <p style={{ margin: 0 }} className="euiTextColor--subdued">
                        {deliveryDefaults.location.comment}
                      </p>
                    )}
                  </EuiText>
                </EuiFormRow>

                <EuiSpacer size="m" />
                <EuiCheckbox
                  id="opsanio-delivery-shipping"
                  checked={deliveryShipping}
                  onChange={(e) => setDeliveryShipping(e.target.checked)}
                  label={
                    <>
                      {translate(
                        "anketa.opsanio.appliance.flyout.documentation.delivery.shipping.label",
                        {
                          defaultMessage:
                            "Also arrange dispatch / freight forwarding",
                        },
                      )}
                      <EuiText size="xs" color="subdued">
                        {translate(
                          "anketa.opsanio.appliance.flyout.documentation.delivery.shipping.help",
                          {
                            defaultMessage:
                              "Inside sales books the forwarder to the address above and coordinates the date with the contact person. Unticked, only the hardware is provided.",
                          },
                        )}
                      </EuiText>
                    </>
                  }
                />

                <EuiSpacer size="m" />
                {deliveryDefaults.contact ? (
                  <EuiFormRow
                    fullWidth
                    label={translate(
                      "anketa.opsanio.appliance.flyout.documentation.delivery.contact.label",
                      { defaultMessage: "Contact person" },
                    )}
                    helpText={deliveryDefaults.contact.label}
                  >
                    <EuiText size="s">
                      <p style={{ margin: 0 }}>
                        <strong>
                          {[
                            deliveryDefaults.contact.name,
                            deliveryDefaults.contact.email,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </strong>
                      </p>
                    </EuiText>
                  </EuiFormRow>
                ) : (
                  <>
                    <EuiCallOut
                      size="s"
                      color="warning"
                      iconType="user"
                      title={translate(
                        "anketa.opsanio.appliance.flyout.documentation.delivery.contact.missing",
                        {
                          defaultMessage:
                            "The questionnaire names no customer contact - please enter one.",
                        },
                      )}
                    />
                    <EuiSpacer size="s" />
                    <EuiFlexGroup gutterSize="s">
                      <EuiFlexItem>
                        <EuiFormRow
                          fullWidth
                          label={translate(
                            "anketa.opsanio.appliance.flyout.documentation.delivery.contact.name",
                            { defaultMessage: "Contact person" },
                          )}
                        >
                          <EuiFieldText
                            fullWidth
                            value={deliveryContactName}
                            onChange={(e) => setDeliveryContactName(e.target.value)}
                          />
                        </EuiFormRow>
                      </EuiFlexItem>
                      <EuiFlexItem>
                        <EuiFormRow
                          fullWidth
                          label={translate(
                            "anketa.opsanio.appliance.flyout.documentation.delivery.contact.email",
                            { defaultMessage: "E-mail (optional)" },
                          )}
                        >
                          <EuiFieldText
                            fullWidth
                            value={deliveryContactEmail}
                            onChange={(e) => setDeliveryContactEmail(e.target.value)}
                          />
                        </EuiFormRow>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </>
                )}

                <EuiSpacer size="m" />
                <EuiFormRow
                  fullWidth
                  label={translate(
                    "anketa.opsanio.appliance.flyout.documentation.delivery.note.label",
                    { defaultMessage: "Note (optional)" },
                  )}
                  helpText={translate(
                    "anketa.opsanio.appliance.flyout.documentation.delivery.note.help",
                    {
                      defaultMessage:
                        "Deadline, deviating delivery address, ticket reference…",
                    },
                  )}
                >
                  <EuiTextArea
                    fullWidth
                    rows={3}
                    value={deliveryNote}
                    maxLength={2000}
                    onChange={(e) => setDeliveryNote(e.target.value)}
                  />
                </EuiFormRow>

              </>
            )}
          </EuiModalBody>

          <EuiModalFooter>
            <EuiButtonEmpty onClick={closeDeliveryDialog} isDisabled={deliverySending}>
              {translate(
                "anketa.opsanio.appliance.flyout.documentation.send.cancel",
                { defaultMessage: "Cancel" },
              )}
            </EuiButtonEmpty>
            <EuiButton
              fill
              iconType="email"
              onClick={sendDeliveryRequest}
              isLoading={deliverySending}
              isDisabled={
                !deliveryDefaults ||
                !deliveryDefaults.recipientConfigured ||
                deliveryCustomer.trim() === "" ||
                deliveryContactMissing
              }
            >
              {translate(
                "anketa.opsanio.appliance.flyout.documentation.delivery.confirm",
                { defaultMessage: "Request" },
              )}
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      )}
    </EuiPanel>
  );
};
